/**
 * Pure selectors + formatters for the jackpot statistics drawer.
 *
 * Kept free of React and of store access so the drawer's filtering rules can be reasoned
 * about (and exercised from Storybook fixtures) in one place.
 */

import {
  JACKPOT_STATISTICS_BOOST_BASE_LEVELS,
  JACKPOT_STATISTICS_LAST_WINS_LIMIT,
  JACKPOT_STATISTICS_LEVEL_KEYS,
  JACKPOT_STATISTICS_LEVEL_RANK,
  JACKPOT_STATISTICS_SORT,
  JACKPOT_STATISTICS_TOP_WINS_LIMIT,
  JACKPOT_STATISTICS_UNRANKED_LEVEL,
} from '../constants';
import type { JackpotStatisticsLevelKey } from '../constants';
import type { IJackpotWinLevel, IJackpotWinRow, JackpotStatisticsSortMode } from '../types/jackpotStatistics.types';

const pad2 = (value: number): string => String(value).padStart(2, '0');

/** `HH:MM:SS` in the player's local time — the top line of the TIME & DATE column. */
export const formatWinTime = (isoDate: string): string => {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return '';
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`;
};

/**
 * `DD/MM/YY` — the second line of the TIME & DATE column. `formatDate` from @repo/common is
 * not reusable here: it emits an unpadded `D/M/YYYY`, the design asks for a padded 2-digit year.
 */
export const formatWinDate = (isoDate: string): string => {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return '';
  return `${pad2(date.getDate())}/${pad2(date.getMonth() + 1)}/${pad2(date.getFullYear() % 100)}`;
};

/** The one name normaliser — jackpot INSTANCE names and level names both compare through it. */
export const normalizeJackpotName = (name: string): string => name.trim().toLowerCase();

export const normalizeJackpotLevelName = (levelName: string): string => normalizeJackpotName(levelName);

/** The canonical level key (`grand` / `major` / `minor` / `mini`), or `null` for any other name. */
export const resolveJackpotLevelKey = (levelName: string): JackpotStatisticsLevelKey | null => {
  const normalized = normalizeJackpotLevelName(levelName);
  return Object.values(JACKPOT_STATISTICS_LEVEL_KEYS).find((key) => key === normalized) ?? null;
};

/**
 * The base level a boost level is treated as (`BoostGrand` → `Grand`), or `null` for a level that
 * is not a boost level. See {@link JACKPOT_STATISTICS_BOOST_BASE_LEVELS}.
 */
const resolveBaseLevelName = (levelName: string): string | null =>
  JACKPOT_STATISTICS_BOOST_BASE_LEVELS[normalizeJackpotLevelName(levelName)] ?? null;

export const formatJackpotLevelLabel = (levelName: string): string =>
  levelName
    .trim()
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toUpperCase();

/** Display rank of one level, by name. Unknown names sort last — see the rank map. */
const levelRank = (level: IJackpotWinLevel): number =>
  JACKPOT_STATISTICS_LEVEL_RANK[normalizeJackpotLevelName(level.levelName)] ?? JACKPOT_STATISTICS_UNRANKED_LEVEL;

const byAmountDesc = (a: IJackpotWinRow, b: IJackpotWinRow): number => b.sortAmount - a.sortAmount;

const byWinTimeDesc = (a: IJackpotWinRow, b: IJackpotWinRow): number =>
  new Date(b.wonAt).getTime() - new Date(a.wonAt).getTime();

const relabelWins = (wins: IJackpotWinRow[], levelName: string): IJackpotWinRow[] =>
  wins.map((win) => ({ ...win, levelName }));

/** One level carrying another level's wins as well — counters summed, lists merged and capped. */
const combineLevels = (base: IJackpotWinLevel, extra: IJackpotWinLevel, levelName: string): IJackpotWinLevel => ({
  ...base,
  levelName,
  totalWins: base.totalWins + extra.totalWins,
  lastWins: [...base.lastWins, ...relabelWins(extra.lastWins, levelName)]
    .sort(byWinTimeDesc)
    .slice(0, JACKPOT_STATISTICS_LAST_WINS_LIMIT),
  topWins: [...base.topWins, ...relabelWins(extra.topWins, levelName)]
    .sort(byAmountDesc)
    .slice(0, JACKPOT_STATISTICS_TOP_WINS_LIMIT),
});

/** The levels as rendered: boost levels merged into their base level, sorted by display rank. */
export const selectDisplayLevels = (levels: IJackpotWinLevel[]): IJackpotWinLevel[] => {
  const displayLevels: IJackpotWinLevel[] = [];
  const indexByLevelName = new Map<string, number>();

  for (const level of levels) {
    // A boost level takes its base level's name; every other level keeps its own.
    const displayName = resolveBaseLevelName(level.levelName) ?? level.levelName;
    const key = normalizeJackpotLevelName(displayName);
    const existingIndex = indexByLevelName.get(key);

    if (existingIndex === undefined) {
      indexByLevelName.set(key, displayLevels.length);
      // Relabel only when the name actually changed, so non-boost levels are passed through
      // untouched and their row objects stay identical to the cached ones.
      displayLevels.push(
        displayName === level.levelName
          ? level
          : {
              ...level,
              levelName: displayName,
              lastWins: relabelWins(level.lastWins, displayName),
              topWins: relabelWins(level.topWins, displayName),
            },
      );
      continue;
    }

    displayLevels[existingIndex] = combineLevels(displayLevels[existingIndex], level, displayName);
  }

  return displayLevels.sort((a, b) => levelRank(a) - levelRank(b));
};

/**
 * The levels the current filter applies to — one, or all of them.
 *
 */
const selectFilteredLevels = (levels: IJackpotWinLevel[], selectedLevelName: string | null): IJackpotWinLevel[] => {
  if (selectedLevelName === null) return levels;

  // The drawer already hands over a normalised name; normalising again keeps the comparison
  // correct for any other caller of the exported selectors.
  const selected = normalizeJackpotLevelName(selectedLevelName);
  return levels.filter((level) => normalizeJackpotLevelName(level.levelName) === selected);
};

/**
 * The rows the content panel renders.
 *
 * Takes the DISPLAY levels ({@link selectDisplayLevels}), not the raw response, so the boost merge
 * has already happened and every row carries the level name it should be shown under.
 *
 * At most ONE level can be selected (the tabs are single-select), so `selectedLevelName` is
 * either one normalised level name or `null` for "all levels".
 *
 */
export const selectVisibleWins = (
  displayLevels: IJackpotWinLevel[],
  sortMode: JackpotStatisticsSortMode,
  selectedLevelName: string | null,
): IJackpotWinRow[] => {
  const levels = selectFilteredLevels(displayLevels, selectedLevelName);

  if (sortMode === JACKPOT_STATISTICS_SORT.TOP_10) {
    return levels
      .flatMap((level) => level.topWins)
      .sort(byAmountDesc)
      .slice(0, JACKPOT_STATISTICS_TOP_WINS_LIMIT);
  }

  return levels
    .flatMap((level) => level.lastWins)
    .sort(byWinTimeDesc)
    .slice(0, JACKPOT_STATISTICS_LAST_WINS_LIMIT);
};

/**
 * The single win shown in the header panel — the FIRST row of the OPPOSITE ordering, per spec:
 * while the list shows LAST 100 the header shows the biggest win, while it shows TOP 10 the
 * header shows the most recent one. The level filter applies to both the same way.
 */
export const selectHeaderWin = (
  displayLevels: IJackpotWinLevel[],
  sortMode: JackpotStatisticsSortMode,
  selectedLevelName: string | null,
): IJackpotWinRow | null => {
  const oppositeMode =
    sortMode === JACKPOT_STATISTICS_SORT.TOP_10 ? JACKPOT_STATISTICS_SORT.LAST_100 : JACKPOT_STATISTICS_SORT.TOP_10;
  return selectVisibleWins(displayLevels, oppositeMode, selectedLevelName)[0] ?? null;
};
